import {
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormLabel,
    Grid,
    MenuItem,
    Radio,
    RadioGroup,
    TextField,
    Typography,
    Divider,
    Alert,
    CircularProgress,
} from "@mui/material";
import { useState } from "react";
import { useMutation } from "react-query";
import { toast } from "react-toastify";
import { createSpecialistRegistration } from "../../../api/services/api";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

export const CreateSpecialistRegistration = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [submitError, setSubmitError] = useState("");

    const [formData, setFormData] = useState({
        // Personal Information
        full_name: "",
        id_number: "",
        date_of_birth: "",
        nationality: "",
        gender: "",
        mobile_number: "",
        email: "",
        address: "",

        // Professional Information
        specialty: [] as string[],
        other_specialty: "",
        professional_classification: "",
        licence_number: "",
        issuing_authority: "",
        licence_expiry_date: "",
        years_of_experience: "",

        // Documents (URLs)
        licence_document_url: "",
        id_document_url: "",
        cv_document_url: "",

        // Declaration
        declaration_accepted: true,
        signature_name: "",
        signature_date: new Date().toISOString().split("T")[0],
    });

    const [errors, setErrors] = useState<any>({});

    const specialtyOptions = [
        "Psychologist",
        "Social Worker",
        "Speech and Language Therapist",
        "Occupational Therapist",
        "Physical Therapist",
        "Other",
    ];

    const { mutate: createMutate } = useMutation(createSpecialistRegistration, {
        onSuccess: (res: any) => {
            toast.success("Specialist registration created successfully");
            navigate("/admin/specialist-registrations");
        },
        onError: (res: any) => {
            toast.error(
                res?.response?.data?.message ||
                "Failed to create specialist registration"
            );
            setSubmitError(
                res?.response?.data?.message ||
                "Failed to create specialist registration"
            );
        },
    });

    const handleInputChange = (e: any) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors((prev: any) => ({ ...prev, [name]: "" }));
        }
    };

    const handleSpecialtyChange = (e: any) => {
        const { value, checked } = e.target;
        setFormData((prev) => {
            const newSpecialty = checked
                ? [...prev.specialty, value]
                : prev.specialty.filter((s) => s !== value);
            return { ...prev, specialty: newSpecialty };
        });
        if (errors.specialty) {
            setErrors((prev: any) => ({ ...prev, specialty: "" }));
        }
    };

    const validateForm = () => {
        const newErrors: any = {};

        // Personal Information Validation
        if (!formData.full_name.trim())
            newErrors.full_name = "Full name is required";
        if (!formData.id_number.trim())
            newErrors.id_number = "ID/Residence number is required";
        if (!formData.date_of_birth)
            newErrors.date_of_birth = "Date of birth is required";
        if (!formData.nationality.trim())
            newErrors.nationality = "Nationality is required";
        if (!formData.gender) newErrors.gender = "Gender is required";
        if (!formData.mobile_number.trim())
            newErrors.mobile_number = "Mobile number is required";
        if (!formData.email.trim()) {
            newErrors.email = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = "Invalid email format";
        }
        if (!formData.address.trim()) newErrors.address = "Address is required";

        // Professional Information Validation
        if (formData.specialty.length === 0)
            newErrors.specialty = "Please select at least one specialty";
        if (
            formData.specialty.includes("Other") &&
            !formData.other_specialty.trim()
        ) {
            newErrors.other_specialty = "Please specify the specialty";
        }
        if (!formData.professional_classification.trim())
            newErrors.professional_classification =
                "Professional classification is required";
        if (!formData.licence_number.trim())
            newErrors.licence_number = "Licence number is required";
        if (!formData.issuing_authority.trim())
            newErrors.issuing_authority = "Issuing authority is required";
        if (!formData.licence_expiry_date)
            newErrors.licence_expiry_date = "Licence expiry date is required";
        if (!formData.years_of_experience)
            newErrors.years_of_experience = "Years of experience is required";

        // Declaration Validation
        if (!formData.signature_name.trim())
            newErrors.signature_name = "Signature name is required";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: any) => {
        e.preventDefault();
        setSubmitError("");

        if (!validateForm()) {
            setSubmitError("Please fill in all required fields correctly.");
            return;
        }

        setLoading(true);

        try {
            const submissionData = {
                ...formData,
                gender: formData.gender.toUpperCase(),
                years_of_experience: parseInt(formData.years_of_experience),
            };

            createMutate(submissionData);
        } catch (error: any) {
            console.error("Submission error:", error);
            setSubmitError(
                error.response?.data?.message ||
                "Failed to create registration. Please try again later."
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            {/* Header */}
            <Box display="flex" alignItems="center" mb={3}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate("/admin/specialist-registrations")}
                    sx={{ mr: 2 }}
                >
                    Back
                </Button>
                <Typography variant="h4" fontWeight="bold">
                    Create Specialist Registration
                </Typography>
            </Box>

            <Card>
                <CardContent sx={{ p: 4 }}>
                    {submitError && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            {submitError}
                        </Alert>
                    )}

                    <form onSubmit={handleSubmit}>
                        {/* Personal Information */}
                        <Typography variant="h6" gutterBottom color="primary" mb={2}>
                            Personal Information
                        </Typography>
                        <Divider sx={{ mb: 3 }} />

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Full Name *"
                                    name="full_name"
                                    value={formData.full_name}
                                    onChange={handleInputChange}
                                    error={!!errors.full_name}
                                    helperText={errors.full_name}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="ID / Residence Number *"
                                    name="id_number"
                                    value={formData.id_number}
                                    onChange={handleInputChange}
                                    error={!!errors.id_number}
                                    helperText={errors.id_number}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Date of Birth *"
                                    name="date_of_birth"
                                    type="date"
                                    value={formData.date_of_birth}
                                    onChange={handleInputChange}
                                    error={!!errors.date_of_birth}
                                    helperText={errors.date_of_birth}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Nationality *"
                                    name="nationality"
                                    value={formData.nationality}
                                    onChange={handleInputChange}
                                    error={!!errors.nationality}
                                    helperText={errors.nationality}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <FormControl component="fieldset" error={!!errors.gender}>
                                    <FormLabel component="legend">Gender *</FormLabel>
                                    <RadioGroup
                                        row
                                        name="gender"
                                        value={formData.gender}
                                        onChange={handleInputChange}
                                    >
                                        <FormControlLabel
                                            value="male"
                                            control={<Radio />}
                                            label="Male"
                                        />
                                        <FormControlLabel
                                            value="female"
                                            control={<Radio />}
                                            label="Female"
                                        />
                                        <FormControlLabel
                                            value="other"
                                            control={<Radio />}
                                            label="Other"
                                        />
                                    </RadioGroup>
                                    {errors.gender && (
                                        <Typography variant="caption" color="error">
                                            {errors.gender}
                                        </Typography>
                                    )}
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Mobile Number *"
                                    name="mobile_number"
                                    value={formData.mobile_number}
                                    onChange={handleInputChange}
                                    error={!!errors.mobile_number}
                                    helperText={errors.mobile_number}
                                    placeholder="+966XXXXXXXXX"
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Email Address *"
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    error={!!errors.email}
                                    helperText={errors.email}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Address / City *"
                                    name="address"
                                    value={formData.address}
                                    onChange={handleInputChange}
                                    error={!!errors.address}
                                    helperText={errors.address}
                                />
                            </Grid>
                        </Grid>

                        {/* Professional Information */}
                        <Typography variant="h6" gutterBottom color="primary" mt={4} mb={2}>
                            Professional Information
                        </Typography>
                        <Divider sx={{ mb: 3 }} />

                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <FormControl component="fieldset" error={!!errors.specialty}>
                                    <FormLabel component="legend">Specialty *</FormLabel>
                                    <FormGroup row>
                                        {specialtyOptions.map((option) => (
                                            <FormControlLabel
                                                key={option}
                                                control={
                                                    <Checkbox
                                                        value={option}
                                                        checked={formData.specialty.includes(option)}
                                                        onChange={handleSpecialtyChange}
                                                    />
                                                }
                                                label={option}
                                                sx={{ minWidth: { xs: "100%", sm: "45%", md: "30%" } }}
                                            />
                                        ))}
                                    </FormGroup>
                                    {errors.specialty && (
                                        <Typography variant="caption" color="error">
                                            {errors.specialty}
                                        </Typography>
                                    )}
                                </FormControl>
                            </Grid>

                            {formData.specialty.includes("Other") && (
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Please specify your specialty *"
                                        name="other_specialty"
                                        value={formData.other_specialty}
                                        onChange={handleInputChange}
                                        error={!!errors.other_specialty}
                                        helperText={errors.other_specialty}
                                    />
                                </Grid>
                            )}

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Professional Classification *"
                                    name="professional_classification"
                                    value={formData.professional_classification}
                                    onChange={handleInputChange}
                                    error={!!errors.professional_classification}
                                    helperText={errors.professional_classification}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Licence Number *"
                                    name="licence_number"
                                    value={formData.licence_number}
                                    onChange={handleInputChange}
                                    error={!!errors.licence_number}
                                    helperText={errors.licence_number}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Issuing Authority *"
                                    name="issuing_authority"
                                    value={formData.issuing_authority}
                                    onChange={handleInputChange}
                                    error={!!errors.issuing_authority}
                                    helperText={errors.issuing_authority}
                                    placeholder="e.g., Saudi Commission for Health Specialties"
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Licence Expiry Date *"
                                    name="licence_expiry_date"
                                    type="date"
                                    value={formData.licence_expiry_date}
                                    onChange={handleInputChange}
                                    error={!!errors.licence_expiry_date}
                                    helperText={errors.licence_expiry_date}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Years of Experience *"
                                    name="years_of_experience"
                                    type="number"
                                    value={formData.years_of_experience}
                                    onChange={handleInputChange}
                                    error={!!errors.years_of_experience}
                                    helperText={errors.years_of_experience}
                                    inputProps={{ min: 0 }}
                                />
                            </Grid>

                            {/* Document URLs */}
                            <Grid item xs={12}>
                                <Typography variant="subtitle1" gutterBottom mt={2}>
                                    Document URLs (Optional)
                                </Typography>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Licence Document URL"
                                    name="licence_document_url"
                                    value={formData.licence_document_url}
                                    onChange={handleInputChange}
                                    placeholder="https://..."
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="ID Document URL"
                                    name="id_document_url"
                                    value={formData.id_document_url}
                                    onChange={handleInputChange}
                                    placeholder="https://..."
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="CV/Resume URL"
                                    name="cv_document_url"
                                    value={formData.cv_document_url}
                                    onChange={handleInputChange}
                                    placeholder="https://..."
                                />
                            </Grid>
                        </Grid>

                        {/* Declaration */}
                        <Typography variant="h6" gutterBottom color="primary" mt={4} mb={2}>
                            Declaration
                        </Typography>
                        <Divider sx={{ mb: 3 }} />

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Signature (Full Name) *"
                                    name="signature_name"
                                    value={formData.signature_name}
                                    onChange={handleInputChange}
                                    error={!!errors.signature_name}
                                    helperText={errors.signature_name}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Date *"
                                    name="signature_date"
                                    type="date"
                                    value={formData.signature_date}
                                    onChange={handleInputChange}
                                    InputLabelProps={{ shrink: true }}
                                    disabled
                                />
                            </Grid>
                        </Grid>

                        {/* Submit Buttons */}
                        <Box display="flex" gap={2} justifyContent="flex-end" mt={4}>
                            <Button
                                variant="outlined"
                                size="large"
                                onClick={() => navigate("/admin/specialist-registrations")}
                                disabled={loading}
                            >
                                Cancel
                            </Button>

                            <Button
                                type="submit"
                                variant="contained"
                                size="large"
                                disabled={loading}
                            >
                                {loading ? (
                                    <CircularProgress size={24} color="inherit" />
                                ) : (
                                    "Create Registration"
                                )}
                            </Button>
                        </Box>
                    </form>
                </CardContent>
            </Card>
        </Box>
    );
};
